import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Drawer, DrawerContent } from './drawer';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * Combobox - Select profesional cu cautare integrata
 * Pe desktop: Popover clasic
 * Pe mobil: Drawer (bottom sheet) pentru UX mai bun
 */
export const Combobox = ({
  items = [],
  value = '',
  onSelect,
  placeholder = 'Selecteaza...',
  searchPlaceholder = 'Cauta...',
  emptyText = 'Niciun rezultat.',
  nameKey = 'name',
  descKey = 'description',
  codeKey = '',
  secondaryKey = '',
  className = '',
  disabled = false,
  clearable = true
}) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const selectedItem = items.find(i => i.id === value);
  const displayText = selectedItem
    ? (selectedItem[nameKey] || selectedItem.denumire || '')
    : '';

  const handleSelect = (itemId) => {
    onSelect?.(itemId === value ? '' : itemId);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect?.('');
  };

  const commandFilter = (val, search) => {
    const item = items.find(i => i.id === val);
    if (!item) return 0;
    const s = search.toLowerCase();
    const name = (item[nameKey] || item.denumire || '').toLowerCase();
    const desc = (item[descKey] || item.descriere || '').toLowerCase();
    const code = codeKey ? (item[codeKey] || '').toLowerCase() : '';
    const secondary = secondaryKey ? (item[secondaryKey] || '').toLowerCase() : '';
    if (name.includes(s) || desc.includes(s) || code.includes(s) || secondary.includes(s)) return 1;
    return 0;
  };

  const triggerButton = (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      disabled={disabled}
      className={cn(
        'w-full h-11 justify-between font-normal text-left',
        !value && 'text-muted-foreground',
        className
      )}
      onClick={isMobile ? () => setOpen(true) : undefined}
    >
      <span className="truncate flex-1 text-left">
        {displayText || placeholder}
      </span>
      <div className="flex items-center gap-1 ml-2 shrink-0">
        {value && clearable && (
          <span
            role="button"
            className="h-4 w-4 inline-flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
            onClick={handleClear}
            onPointerDown={(e) => e.preventDefault()}
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronsUpDown className="h-4 w-4 opacity-50" />
      </div>
    </Button>
  );

  const commandList = (
    <Command filter={commandFilter}>
      <CommandInput placeholder={searchPlaceholder} />
      <CommandList className="max-h-[300px] overflow-y-auto">
        <CommandEmpty>{emptyText}</CommandEmpty>
        <CommandGroup>
          {items.map((item) => (
            <CommandItem
              key={item.id}
              value={item.id}
              onSelect={handleSelect}
              className="cursor-pointer"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {item[nameKey] || item.denumire || ''}
                  </span>
                  {codeKey && item[codeKey] && (
                    <span className="text-[10px] text-slate-400 font-mono shrink-0">
                      {item[codeKey]}
                    </span>
                  )}
                </div>
                {(item[descKey] || item.descriere) && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {item[descKey] || item.descriere}
                  </p>
                )}
                {secondaryKey && item[secondaryKey] && (
                  <p className="text-xs text-slate-400 truncate">
                    {item[secondaryKey]}
                  </p>
                )}
              </div>
              <Check
                className={cn(
                  'ml-2 h-4 w-4 shrink-0',
                  value === item.id ? 'opacity-100 text-blue-600' : 'opacity-0'
                )}
              />
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  // Mobile: Drawer (bottom sheet)
  if (isMobile) {
    return (
      <>
        {triggerButton}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="px-2 pb-6">
            <div className="mt-2">
              {commandList}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // Desktop: Popover
  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        {triggerButton}
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        side="bottom"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {commandList}
      </PopoverContent>
    </Popover>
  );
};

export default Combobox;
